// app/javascript/controllers/cart_button_controller.js
import { Controller } from "@hotwired/stimulus";
import Toastify from "toastify-js";

/**
 * Stimulus controller responsible for adding/removing items from the cart and
 * keeping the Stripe PaymentIntent in sync (via /update_checkout).
 *
 * Improvements over the previous version:
 * 1.   After every cart mutation we automatically call `updatePaymentIntent` so
 *      the checkout sheet always reflects the latest total.
 * 2.   Handles the `{ emptyCart: true }` payload returned by the back‑end when
 *      the cart is empty or fully‑discounted, thereby avoiding JS errors and
 *      unnecessary calls to Stripe.
 * 3.   Small refactors: DRY toast helpers, consolidated fetch wrapper, early
 *      returns for clarity.
 */
export default class extends Controller {
  static values = {
    itemType: String,
    itemId: String,
    cartItemId: String
  };

  /* ----------------------------------------------------------- helpers */

  getCsrfToken() {
    return document.querySelector("meta[name='csrf-token']").content;
  }

  toast(text, type = "success", duration = 4000) {
    Toastify({
      text,
      duration,
      close: true,
      position: "right",
      className: type,
      stopOnFocus: true
    }).showToast();
  }

  updateCartCountDisplay(count) {
    document.querySelectorAll(".cart-items-count").forEach(el => (el.textContent = count));
  }

  updateSubtotalDisplay(subtotal) {
    document.querySelectorAll(".subtotal-display").forEach(el => (el.textContent = subtotal));
  }

  async jsonFetch(url, options = {}) {
    const res = await fetch(url, options);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  /* ------------------------------------------------------------- actions */

  async addToCart(event) {
    event.preventDefault();

    try {
      const data = await this.jsonFetch("/cart_items", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCsrfToken()
        },
        body: JSON.stringify({
          item_type: this.itemTypeValue,
          item_id: this.itemIdValue,
          authenticity_token: this.getCsrfToken()
        })
      });

      this.updateCartCountDisplay(data.cart_items_count);
      this.updateSubtotalDisplay(data.total_price);
      this.toast("Item added to cart successfully.");

      // Keep the PaymentIntent in sync.
      await this.updatePaymentIntent();
    } catch (err) {
      console.error(err);
      this.toast("There was an issue adding the item to the cart.", "error");
    }
  }

  confirmRemove(event) {
    event.preventDefault();
    if (confirm("Are you sure you want to remove this item?")) {
      this.removeFromCart(event);
    }
  }

  async removeFromCart(event) {
    event.preventDefault();

    const itemElement = document.getElementById(
        `item-${this.itemTypeValue}-${this.cartItemIdValue}`
    );
    if (!itemElement) return;

    try {
      const data = await this.jsonFetch(`/cart_items/${this.cartItemIdValue}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": this.getCsrfToken(),
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ authenticity_token: this.getCsrfToken() })
      });

      this.updateCartCountDisplay(data.cart_items_count);
      this.updateSubtotalDisplay(data.total_price);
      itemElement.remove();
      this.toast("Cart item removed successfully.");

      // Sync the PaymentIntent after removal.
      await this.updatePaymentIntent();
    } catch (err) {
      console.error(err);
      this.toast("Unable to remove cart item.", "error");
    }
  }

  /**
   * Calls /update_checkout so the back‑end recalculates the PaymentIntent.
   * If the cart is empty or totals to 0 the API now returns `{ emptyCart: true
   * }` and we simply stop — no Stripe update required.
   */
  async updatePaymentIntent() {
    try {
      const data = await this.jsonFetch("/update_checkout", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCsrfToken()
        }
      });

      if (data.emptyCart) {
        console.log("Cart empty/zero — skipping PaymentIntent update");
        window.dispatchEvent(new CustomEvent("cart-empty"));
        document.querySelectorAll(".payment-form-container").forEach(el => {
          el.innerHTML = '<div class="p-4 bg-yellow-50 border border-yellow-200 rounded"><p class="text-yellow-700">Your cart is empty or all items are fully discounted. Please add items to your cart to continue.</p></div>';
        });
        return;
      }

      if (data.clientSecret) {
        window.dispatchEvent(
            new CustomEvent("payment-intent-updated", {
              detail: { clientSecret: data.clientSecret }
            })
        );
      }
    } catch (err) {
      console.error("Error updating payment intent:", err);
    }
  }
}
